"use client";
import type { FurnitureAgentJob, FurnitureAgentStep } from "@/types/furniture-agent";
import { STEP_META, HUMANIZE_BACKEND_TYPE } from "@/lib/furniture-agent/steps";
import React from "react";

export function PipelineJobCard({ job }: { job: FurnitureAgentJob }) {
  const humanType = HUMANIZE_BACKEND_TYPE[job.backendType] ?? job.backendType;

  return (
    <div style={{ borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#f8fafc' }}>
            {humanType} <span style={{ color: '#64748b', fontWeight: 400 }}>· {job.displayName}</span>
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
            ID: {job.jobId.slice(0, 8)} · {new Date(job.createdAt).toLocaleString("ko-KR")}
          </div>
        </div>
        <StatusBadge status={job.status} />
      </header>

      {/* 전체 진행률 */}
      <div style={{ marginTop: 12, height: 6, width: '100%', borderRadius: 999, background: 'rgba(255,255,255,0.05)', overflow: 'hidden' }}>
        <div
          style={{ height: '100%', borderRadius: 999, background: '#f97316', transition: 'width 300ms ease-in-out', width: `${Math.round(job.overallProgress * 100)}%` }}
        />
      </div>

      {/* 7스텝 수직 타임라인 */}
      <ol style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none', padding: 0 }}>
        {job.steps.map((s, idx) => (
          <StepRow key={s.id} step={s} index={idx} isCurrent={job.currentStepId === s.id} />
        ))}
      </ol>
    </div>
  );
}

function StepRow({
  step,
  index,
  isCurrent,
}: {
  step: FurnitureAgentStep;
  index: number;
  isCurrent: boolean;
}) {
  const meta = STEP_META[step.id];
  return (
    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <StepDot status={step.status} isCurrent={isCurrent} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: isCurrent ? 600 : 400,
              color: isCurrent ? '#ea580c' : (step.status === 'pending' ? '#64748b' : '#e2e8f0'),
            }}
          >
            {index + 1}. {meta.icon} {meta.label}
          </span>
          {step.latencyMs != null && (
            <span style={{ fontSize: 10, color: '#64748b' }}>{(step.latencyMs / 1000).toFixed(1)}s</span>
          )}
        </div>
        {step.message && (
          <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>↳ {step.message}</div>
        )}
        {step.status === "failed" && (
          <div style={{ marginTop: 4, fontSize: 11, color: '#ef4444' }}>
            ⚠ {step.errorCode ?? "UNKNOWN"} · {step.errorMessage ?? "실패"}
          </div>
        )}
        {step.status === "running" && step.progress != null && (
          <div style={{ marginTop: 4, height: 4, width: '100%', borderRadius: 4, background: 'rgba(255,255,255,0.05)' }}>
            <div
              style={{ height: '100%', borderRadius: 4, background: '#fb923c', width: `${Math.round(step.progress * 100)}%` }}
            />
          </div>
        )}
      </div>
    </li>
  );
}

function StepDot({
  status,
  isCurrent,
}: {
  status: FurnitureAgentStep["status"];
  isCurrent: boolean;
}) {
  const bg =
    status === "success"
      ? "#10b981"
      : status === "failed"
      ? "#ef4444"
      : status === "running"
      ? "#f97316"
      : status === "skipped"
      ? "#cbd5e1"
      : "rgba(255,255,255,0.1)";
      
  return (
    <span
      className={status === "running" ? "animate-pulse" : ""}
      style={{
        marginTop: 4, display: 'inline-block', height: 10, width: 10, borderRadius: '50%', background: bg,
        boxShadow: isCurrent ? '0 0 0 2px rgba(251, 146, 60, 0.3)' : 'none'
      }}
    />
  );
}

function StatusBadge({ status }: { status: FurnitureAgentJob["status"] }) {
  const map: Record<FurnitureAgentJob["status"], string> = {
    queued: "background: rgba(255,255,255,0.1); color: #94a3b8",
    running: "background: rgba(249,115,22,0.15); color: #fb923c",
    succeeded: "background: rgba(16,185,129,0.15); color: #34d399",
    failed: "background: rgba(239,68,68,0.15); color: #f87171",
    canceled: "background: rgba(255,255,255,0.1); color: #94a3b8",
  };
  
  const bgStyle = map[status];
  return (
    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 500, cssText: bgStyle } as React.CSSProperties}>
      {status}
    </span>
  );
}
